import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Paperclip, ShieldCheck, Landmark } from "lucide-react";

/**
 * Modale d'envoi d'un devis par email.
 * - Email destinataire pré-rempli (modifiable)
 * - Message personnalisable (template par défaut chargé)
 * - Sélecteur de RIB (FR / IE / aucun) — injecté dans le PDF et l'email
 * - Case "joindre Décennale & RC Pro"
 */
export default function DevisSendDialog({ devis, client, onClose, onSent }) {
  const [email, setEmail] = useState(client?.email || "");
  const [message, setMessage] = useState(
    `Bonjour ${client?.nom || "Madame, Monsieur"},

Suite à notre échange, vous trouverez ci-joint le devis n° ${devis.numero} détaillant l'ensemble des prestations convenues. Ce document précise les matériaux retenus, la nature de l'ouvrage et le calendrier d'exécution.

Ce devis est valable ${devis.validite_jours || 30} jours. Pour valider la commande et réserver le créneau d'atelier, il suffit de me retourner le document signé accompagné d'un acompte de 30 % du montant TTC. Les attestations Décennale et RC Pro sont à votre disposition sur simple demande.

Je reste à votre disposition pour tout ajustement, précision ou question complémentaire — par téléphone au 07 61 05 10 61, ou en réponse à ce courriel.

Vous remerciant pour votre confiance,

Bien cordialement,
Luigi Chauveau`
  );
  const [includeAtt, setIncludeAtt] = useState(false);
  const [ribChoice, setRibChoice] = useState("fr");
  const [settings, setSettings] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/admin/parametres")
      .then((r) => setSettings(r.data || {}))
      .catch(() => setSettings({}));
  }, []);

  const hasAttestations = !!(settings.decennale_url || settings.rc_pro_url);
  const hasIbanFr = !!settings.iban;
  const hasIbanIe = !!settings.iban_secondary;

  // Once settings load, pick the best default RIB.
  useEffect(() => {
    if (hasIbanFr) setRibChoice("fr");
    else if (hasIbanIe) setRibChoice("ie");
    else setRibChoice("none");
  }, [hasIbanFr, hasIbanIe]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Adresse email requise");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/devis/${devis.id}/send`, {
        email: email.trim(),
        message: message.trim(),
        include_attestations: includeAtt,
        rib_choice: ribChoice,
      });
      if (data?.status === "sent") {
        toast.success("Devis envoyé");
        onSent && onSent();
        onClose();
      } else if (data?.status === "skipped") {
        toast.info("Envoi simulé — la clé Resend n'est pas configurée dans .env");
        onClose();
      } else {
        toast.error("Échec de l'envoi : " + (data?.error || "erreur"));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="devis-send-dialog"
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-background border border-border w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <header className="flex items-start justify-between px-7 pt-7 pb-5 border-b border-border">
          <div>
            <p className="text-eyebrow mb-2">Envoi par email</p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Devis {devis.numero}
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-1">{devis.titre}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="devis-send-close">
            <X size={20} />
          </button>
        </header>

        <div className="px-7 py-6 space-y-6">
          <div>
            <label className="text-serial block mb-2">Destinataire</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
              data-testid="devis-send-email"
            />
          </div>

          <div>
            <label className="text-serial block mb-2">Message d'accompagnement</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="w-full bg-muted/40 border border-border focus:border-primary p-3.5 text-[14px] leading-relaxed outline-none font-serif"
              data-testid="devis-send-message"
            />
            <p className="text-[11.5px] text-muted-foreground mt-1.5">
              Le PDF du devis sera systématiquement joint.
            </p>
          </div>

          {/* RIB selector */}
          <div className="border-t border-border pt-5">
            <p className="text-serial mb-3 flex items-center gap-2">
              <Landmark size={14} className="text-primary" />
              Coordonnées bancaires à inclure
            </p>
            <div className="grid grid-cols-1 gap-2">
              <label
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${ribChoice === "fr" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"} ${!hasIbanFr ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="rib"
                  value="fr"
                  checked={ribChoice === "fr"}
                  disabled={!hasIbanFr}
                  onChange={() => setRibChoice("fr")}
                  className="mt-1 accent-primary"
                  data-testid="rib-choice-fr"
                />
                <div className="flex-1">
                  <p className="text-[13.5px] font-medium">🇫🇷 {settings.banque_label || "Compte français"}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {hasIbanFr ? `${settings.banque_nom || "Banque"} · IBAN ${settings.iban}` : "IBAN à renseigner dans Paramètres"}
                  </p>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${ribChoice === "ie" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"} ${!hasIbanIe ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="rib"
                  value="ie"
                  checked={ribChoice === "ie"}
                  disabled={!hasIbanIe}
                  onChange={() => setRibChoice("ie")}
                  className="mt-1 accent-primary"
                  data-testid="rib-choice-ie"
                />
                <div className="flex-1">
                  <p className="text-[13.5px] font-medium">🇮🇪 {settings.banque_secondary_label || "Compte irlandais"}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {hasIbanIe ? `${settings.banque_secondary_nom || "Banque"} · IBAN ${settings.iban_secondary}` : "IBAN à renseigner dans Paramètres"}
                  </p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${ribChoice === "none" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}>
                <input
                  type="radio"
                  name="rib"
                  value="none"
                  checked={ribChoice === "none"}
                  onChange={() => setRibChoice("none")}
                  className="mt-1 accent-primary"
                  data-testid="rib-choice-none"
                />
                <div className="flex-1">
                  <p className="text-[13.5px] font-medium">Aucun</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">Le devis sera envoyé sans coordonnées bancaires.</p>
                </div>
              </label>
            </div>
          </div>

          {/* Attestations */}
          <div className="border-t border-border pt-5">
            <label
              className={`flex items-start gap-4 cursor-pointer ${!hasAttestations ? "opacity-50 cursor-not-allowed" : ""}`}
              title={!hasAttestations ? "Importez d'abord vos attestations PDF dans Paramètres" : ""}
            >
              <input
                type="checkbox"
                checked={includeAtt}
                disabled={!hasAttestations}
                onChange={(e) => setIncludeAtt(e.target.checked)}
                className="h-4 w-4 accent-primary mt-0.5"
                data-testid="devis-send-attestations"
              />
              <div>
                <p className="text-[14px] font-medium flex items-center gap-2">
                  <ShieldCheck size={15} className="text-primary" />
                  Joindre les attestations Décennale et RC Pro
                </p>
                <p className="text-[12.5px] text-muted-foreground mt-1.5">
                  {hasAttestations
                    ? "Les PDFs uploadés dans Paramètres seront ajoutés en pièce jointe."
                    : "Aucune attestation n'a encore été importée — rendez-vous dans Paramètres → Attestations."}
                </p>
              </div>
            </label>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-4 px-7 py-5 border-t border-border bg-muted/20">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] tracking-[0.15em] uppercase text-muted-foreground link-underline-static"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-terra inline-flex items-center gap-2"
            disabled={sending}
            data-testid="devis-send-submit"
          >
            {sending ? (
              <>
                <Paperclip size={14} className="animate-pulse" /> Envoi en cours…
              </>
            ) : (
              <>
                <Send size={14} /> Envoyer
              </>
            )}
          </button>
        </footer>
      </form>
    </div>
  );
}
